import mixpanel from "mixpanel-browser";
import { getDeviceInfo, getBasicDeviceInfo, getDevicePerformanceInfo } from "../utils/TrackingUtils";

let isInitialized = false;
let deviceInfo: any = null;

export const initializeMixPanel = () => {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN;

    if (!token) {
        console.warn('MixPanel token not found in environment variables');
        return;
    }

    mixpanel.init(token, {
        debug: import.meta.env.MODE === 'development',
        track_pageview: true,
        persistence: 'localStorage',
        ignore_dnt: true,
    });

    // Collect device information once during initialization
    deviceInfo = getBasicDeviceInfo();

    // Set super properties that will be included with every event
    mixpanel.register({
        ...deviceInfo,
        // Add performance info as super properties
        ...getDevicePerformanceInfo()
    });

    isInitialized = true;
};

export const track = (eventName: string, properties?: Record<string, any>) => {
    if (!isInitialized) {
        console.warn('MixPanel not initialized. Skipping event:', eventName);
        return;
    }

    // Device info is automatically included via super properties
    mixpanel.track(eventName, properties);

    console.log('track', eventName);
};


/**
 * Track an event with full device information (for detailed analytics)
 */
export const trackWithFullDeviceInfo = (eventName: string, properties?: Record<string, any>) => {
    if (!isInitialized) {
        console.warn('MixPanel not initialized. Skipping event:', eventName);
        return;
    }

    const fullDeviceInfo = getDeviceInfo();
    track(eventName, {
        ...properties,
        ...fullDeviceInfo
    });
};

/**
 * Track user session with device context
 */
export const trackSession = (sessionType: 'start' | 'end', additionalProperties?: Record<string, any>) => {
    const sessionEvent = sessionType === 'start' ? 'session_start' : 'session_end';

    trackWithFullDeviceInfo(sessionEvent, {
        timestamp: new Date().toISOString(),
        sessionType,
        ...additionalProperties
    });
};

/**
 * Track page view with device context
 */
export const trackPageView = (pageName: string, additionalProperties?: Record<string, any>) => {
    track('page_view', {
        page: pageName,
        timestamp: new Date().toISOString(),
        ...additionalProperties
    });
};

/**
 * Track user interaction with device context
 */
export const trackInteraction = (interactionType: string, element: string, additionalProperties?: Record<string, any>) => {
    track('user_interaction', {
        interactionType,
        element,
        timestamp: new Date().toISOString(),
        ...additionalProperties
    });
};

/**
 * Update device info (useful for responsive design changes)
 */
export const updateDeviceInfo = () => {
    if (!isInitialized) {
        console.warn('MixPanel not initialized. Cannot update device info.');
        return;
    }

    deviceInfo = getBasicDeviceInfo();
    mixpanel.register(deviceInfo);
};

export const identify = (userId: string, userProperties?: Record<string, any>) => {
    if (!isInitialized) {
        console.warn('MixPanel not initialized. Skipping identify.');
        return;
    }

    mixpanel.identify(userId);

    // Set user properties including device info
    if (userProperties) {
        mixpanel.people.set({
            ...userProperties,
            $device_id: deviceInfo?.deviceId,
            $device_type: deviceInfo?.deviceType,
            $os: deviceInfo?.deviceOS,
            $browser: deviceInfo?.browser,
            $screen_size: deviceInfo?.screenSize,
            $language: deviceInfo?.language,
            last_seen: new Date().toISOString()
        });
    }
};

/**
 * Get current device info being used for tracking
 */
export const getCurrentDeviceInfo = () => {
    return deviceInfo;
};
